import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import {
  FLOW_ORANGE_ACTION_BUTTON_BASE,
  FLOW_TEXT_INPUT_BASE,
} from "../../../styles/twDesignPrimitives";
import { cn } from "../../../utils/cn";
import {
  formatAgendaClockNow,
  formatAgendaLongDate,
} from "../agendaDateFormat";
import type { ScheduleSearchItem } from "../../../types/scheduleCard";
import {
  formatScheduleCardStatus,
  formatScheduleDateTime,
  formatPersonName,
  formatPropertyLabel,
  formatPropertyAddress,
} from "../../../utils/scheduleSearchDisplay";

type AgendaHeaderProps = {
  selectedDateIso: string;
  search: string;
  onSearchChange: (value: string) => void;
  searchLoading: boolean;
  searchResults: ScheduleSearchItem[];
  onOpenSearchResult: (item: ScheduleSearchItem) => void;
  onNewSchedule: () => void;
  onBulkBlock: () => void;
  canManageSchedules: boolean;
  layout?: "desktop" | "tablet";
};

const AgendaHeader: React.FC<AgendaHeaderProps> = ({
  selectedDateIso,
  search,
  onSearchChange,
  searchLoading,
  searchResults,
  onOpenSearchResult,
  onNewSchedule,
  canManageSchedules,
  layout = "desktop",
}) => {
  const isTablet = layout === "tablet";
  const [clockLabel, setClockLabel] = useState(formatAgendaClockNow());

  useEffect(() => {
    setClockLabel(formatAgendaClockNow());
    const timer = window.setInterval(() => {
      setClockLabel(formatAgendaClockNow());
    }, 60_000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <header className={isTablet ? "mb-4" : "mb-7"}>
      <div
        className={cn(
          "flex flex-col gap-2",
          isTablet
            ? "md:flex-row md:items-center md:justify-between md:gap-3"
            : "lg:flex-row lg:items-center lg:justify-between lg:gap-4"
        )}
      >
        <h1
          className={cn(
            "shrink-0 font-semibold leading-tight text-slate-800",
            isTablet ? "text-[22px]" : "text-[26px]"
          )}
        >
          Agenda de Visitas
        </h1>

        <div
          className={cn(
            "flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:justify-end",
            isTablet ? "md:max-w-none md:flex-1" : "lg:max-w-[720px] lg:flex-1"
          )}
        >
          <div className="relative min-w-0 flex-1 sm:max-w-md">
            <input
              type="search"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Buscar cliente, imóvel ou e-mail"
              className={cn(FLOW_TEXT_INPUT_BASE, "h-10 text-sm !font-[Montserrat,sans-serif]")}
            />
            {search.trim().length > 0 ? (
              <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-20 max-h-72 overflow-y-auto rounded-md border border-[#E2E8F0] bg-white p-1.5 shadow-lg">
                {searchLoading ? (
                  <p className="px-2 py-1.5 text-xs text-[#718096]">Buscando...</p>
                ) : null}
                {!searchLoading && searchResults.length === 0 ? (
                  <p className="px-2 py-1.5 text-xs text-[#718096]">
                    Nenhum agendamento encontrado
                  </p>
                ) : null}
                {!searchLoading
                  ? searchResults.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => onOpenSearchResult(item)}
                        className="mb-0.5 w-full rounded-md border border-[#EDF2F7] px-2 py-1.5 text-left last:mb-0 hover:bg-[#F7FAFC]"
                      >
                        <p className="text-xs font-semibold text-[#2D3748]">
                          {formatPersonName(item.client)}
                        </p>
                        <p className="text-[11px] text-[#718096]">
                          {formatScheduleDateTime(item.date)} ·{" "}
                          {formatPropertyLabel(item.property)}
                        </p>
                        <p className="text-[11px] text-[#718096]">
                          {formatPropertyAddress(item.property?.address)} ·{" "}
                          {formatScheduleCardStatus(item)}
                        </p>
                      </button>
                    ))
                  : null}
              </div>
            ) : null}
          </div>

          {canManageSchedules ? (
            <div className="flex shrink-0 flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={onNewSchedule}
                className={cn(
                  FLOW_ORANGE_ACTION_BUTTON_BASE,
                  "h-10 gap-1.5 px-3 text-sm"
                )}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                Novo agendamento
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {!isTablet ? (
        <p className="mt-1.5 text-[15px] font-medium text-slate-600">
          {formatAgendaLongDate(selectedDateIso)} · agora {clockLabel}
        </p>
      ) : null}
    </header>
  );
};

export default AgendaHeader;
